import type { CSSProperties } from 'react';
import { tierColorFor, tokens } from '../theme';

/**
 * Badge hạng thẻ — tô màu theo MÃ hạng (bronze/silver/gold/diamond), không theo tên hiển thị.
 *
 * <p>🔴 Trước đây mọi hạng đều là `StatusTag color="blue"` nên Đồng/Bạc/Vàng/Kim Cương trông giống
 * hệt nhau. Nay mỗi hạng một sắc riêng qua {@link tierColorFor} — cùng bảng màu đã dùng cho chấm hạng
 * ở Dashboard và RuleFormPanel, nên toàn app nói cùng một thứ tiếng về màu hạng.</p>
 *
 * <p>📌 <b>Màu ở CHẤM + nền tint, chữ để mực đậm.</b> Bạc (`#94A3B8`) quá nhạt để làm chữ (rớt WCAG
 * AA), nên màu hạng chỉ nằm ở chấm + viền + nền mờ; tên hạng dùng `ink` đọc rõ ở mọi hạng. Màu KHÔNG
 * phải tín hiệu duy nhất — tên hạng luôn hiện kèm (quy tắc 8, 09-design-system).</p>
 */

interface TierBadgeProps {
  /** Mã hạng đã khoá (bronze/silver/gold/diamond). `null`/rỗng = chưa xếp hạng. */
  code: string | null | undefined;
  /** Tên hiển thị đã phân giải (marketing đặt được), ví dụ "Vàng". Với chưa có hạng thì "Chưa có hạng". */
  name: string;
  style?: CSSProperties;
}

export function TierBadge({ code, name, style }: TierBadgeProps) {
  // Chưa xếp hạng: chữ mờ, KHÔNG dựng pill — pill trông như một hạng tên là "Chưa có hạng".
  if (!code) {
    return (
      <span style={{ color: tokens.color.ink3, fontSize: 12.5, ...style }}>{name}</span>
    );
  }

  const color = tierColorFor(code);

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '1px 10px 1px 8px',
        borderRadius: tokens.radius.pill,
        // Hex 8 số: 2 số cuối là alpha. `1A`≈10% nền, `59`≈35% viền — đủ để nhận sắc, không chói.
        background: `${color}1A`,
        border: `1px solid ${color}59`,
        color: tokens.color.ink,
        fontSize: 12.5,
        fontWeight: 600,
        lineHeight: '18px',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      <span
        aria-hidden
        style={{ width: 8, height: 8, borderRadius: 999, background: color, flex: 'none' }}
      />
      {name}
    </span>
  );
}
